import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import PropTypes from "prop-types";

const Navbar = ({ variant = "dark" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const location = useLocation();

  // Define isActive function
  const isActive = (path) => location.pathname === path;

  // Determine colors based on variant
  const isDark = variant === "dark";
  const bgColor = isDark ? "var(--primary-color)" : "#ffffff";
  //const textColor = isDark ? "text-white" : "var(--primary-color)";
  const textColor = isDark ? "text-white" : "text-[color:var(--primary-color)]";
  const hoverColor = isDark ? "hover:text-gray-200" : "hover:text-gray-600";
  const mobileHoverBg = isDark ? "hover:bg-blue-800" : "hover:bg-gray-100";
  const activeLineColor = isDark ? "bg-white" : "bg-blue-600";
  const mobileDropdownBg = isDark ? "bg-blue-800" : "bg-gray-50";
  const mobileDropdownHoverBg = isDark
    ? "hover:bg-blue-900"
    : "hover:bg-gray-100";

  // Select logo based on variant
  const logoSrc = isDark
    ? "/assets/images/avexa-logo-header.svg"
    : "/assets/images/avexa-white-logo.svg";

  const navLinks = [
    { path: "/", label: "Home" },

    {
      path: "#",
      label: "Platforms",
      dropdown: [
        { path: "/one-care", label: "Avexa OneCare" },
        // { path: '/digital-health-platform', label: 'Digital Health Platform' },
      ],
    },
    {
      path: "#",
      label: "Solutions",
      dropdown: [
        { path: "/digital-health", label: "Digital Health Suites" },
        { path: "/digital-transformation", label: "Digital Transformation" },
        // { path: "/edg", label: "EDG" },
      ],
    },
    { path: "/research", label: "Research On" },
    { path: "/about-us", label: "Who we are" },
    { path: "/get-in-touch", label: "Get In Touch" },
  ];

  return (
    <nav
      style={{ backgroundColor: bgColor }}
      className={`fixed top-0 left-0 right-0 w-full shadow-lg ${!isDark && "border-b border-gray-200"} z-50`}
    >
      <div className="mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-16">
          {/* Logo - Changes based on variant */}
          <Link to="/" className="flex items-center">
            <img src={logoSrc} alt="Avexa Logo" className="h-8 w-auto" />
          </Link>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className={`lg:hidden ${textColor} focus:outline-none`}
            aria-label="Toggle menu"
          >
            <svg
              className="h-6 w-6"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              {isOpen ? (
                <path d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex lg:items-center lg:space-x-8">
            {navLinks.map((link) =>
              link.dropdown ? (
                <div
                  key={link.path}
                  className="relative group"
                  onMouseEnter={() => setDropdownOpen(true)}
                  onMouseLeave={() => setDropdownOpen(false)}
                >
                  <button
                    className={`relative px-1  transition  ${textColor} ${hoverColor}`}
                  >
                    {link.label}
                    {isActive(link.path) && (
                      <span
                        className={`absolute bottom-0 left-0 right-0 h-0.5 ${activeLineColor}`}
                      ></span>
                    )}
                  </button>

                  {/* Dropdown Menu - Maximum visibility with strong shadow */}
                  <div
                    className="absolute left-0 mt-2 w-56 rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200"
                    style={{
                      backgroundColor: "#ffffff",
                      boxShadow: "0 10px 40px rgba(0, 0, 0, 0.3)",
                      zIndex: 9999,
                    }}
                  >
                    {link.dropdown.map((item, index) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`block px-4 py-3 text-gray-900  transition ${
                          index === 0 ? "rounded-t-lg" : ""
                        } ${
                          index === link.dropdown.length - 1
                            ? "rounded-b-lg"
                            : ""
                        } ${isActive(item.path) ? "font-bold" : ""}`}
                        style={{
                          backgroundColor: isActive(item.path)
                            ? "#E3F2FD"
                            : "transparent",
                        }}
                        onMouseEnter={(e) => {
                          if (!isActive(item.path)) {
                            e.currentTarget.style.backgroundColor = "#F0F4FF";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isActive(item.path)) {
                            e.currentTarget.style.backgroundColor =
                              "transparent";
                          }
                        }}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`relative px-1  transition  ${textColor} ${hoverColor}`}
                  // style={{ color: textColor }}
                >
                  {link.label}
                  {isActive(link.path) && (
                    <span
                      className={`absolute bottom-0 left-0 right-0 h-0.5 ${activeLineColor}`}
                    ></span>
                  )}
                </Link>
              ),
            )}
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="lg:hidden pb-4">
            {navLinks.map((link) =>
              link.dropdown ? (
                <div key={link.path}>
                  <button
                    onClick={() =>
                      setDropdownOpen(
                        dropdownOpen === link.label ? null : link.label,
                      )
                    }
                    className={`block w-full text-left px-4 py-3   font-medium  ${textColor}  ${mobileHoverBg} transition`}
                    // style={{ color: textColor }}
                  >
                    {link.label}
                    <svg
                      className={`inline-block w-4 h-4 ml-1 transform transition-transform ${
                        dropdownOpen === link.label ? "rotate-180" : ""
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  {/* {dropdownOpen && ( */}
                  {dropdownOpen === link.label && (
                    <div className={mobileDropdownBg}>
                      {link.dropdown.map((item) => (
                        <Link
                          key={item.path}
                          to={item.path}
                          className={`block px-8 py-3    ${textColor} ${mobileDropdownHoverBg} transition`}
                          onClick={() => setIsOpen(false)}
                          // style={{ color: textColor }}
                        >
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`block px-4 py-3 ${textColor}  font-medium  ${textColor} ${mobileHoverBg} transition`}
                  onClick={() => setIsOpen(false)}
                  //style={{ color: textColor }}
                >
                  {link.label}
                </Link>
              ),
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

Navbar.propTypes = {
  variant: PropTypes.oneOf(["dark", "light"]),
};

export default Navbar;
